import { useEffect, useState } from "react";
import { useResourceContext } from "@/layouts/ResourceContext";
import { Badge, Col, Flex, Row, Select, Space, Table, Tooltip, Typography } from "antd"
import { useTable } from "@refinedev/antd";
import { Area } from '@ant-design/plots';
import { useInvalidate, useList } from "@refinedev/core";

import { getTopology as getTopologyAPI } from "@/apis/instance";
import { RightOutlined } from "@ant-design/icons";
import { RefreshButton } from "@/components/common/RefreshButton";
import WaveformSVG from "../control/WaveformSVG";
import { useOne } from "@refinedev/core";
import DataList from "../control/DataList";
import DonutChart from "../control/DonutChart";
import MetricsChart from "../control/MetricsChart";

const toGUnit = (bytes) => {
  let gi = bytes/1073741824;
  return gi.toFixed(2) + "G";
}

const getInitialScope = (selectedResource, mode)=> {
  if("pool" == mode)
    return "pods";
  else if("nodegroup" == mode)
    return "nodes";
  else if("instance" == mode) {
    if(selectedResource.type == "silo") {
      return "nodes";
    } else {
      return "pods";
    }
  }
}

const getInitialFilter = (selectedResource, mode, isNode) => {
  if("pool" == mode)
    return [{field: "namespace", operator: "contains", value: [selectedResource?.id]}];
  else if("nodegroup" == mode)
    return [{field: "nodegroup", operator: "contains", value: [selectedResource?.id]}];
  else if("instance" == mode) {
    if(selectedResource.type == "silo") {
      if(isNode)
        return [{field: "nodegroup", operator: "contains", value: [selectedResource?.nodegroup]}];
      else
        return [{field: "namespace", operator: "contains", value: [selectedResource?.namespace]}]
    } else {
      return [{field: "namespace", operator: "contains", value: [selectedResource?.pools]}];
    }
  }
}

const calculateTimeDifference = (createdDate) => {
  const now = new Date();
  const differenceInMilliseconds = now - createdDate;

  if (differenceInMilliseconds == 0) {
    return "now";
  }
  const differenceInMinutes = Math.floor(differenceInMilliseconds / (1000 * 60));
  if (differenceInMinutes < 60)
    return differenceInMinutes + " " + (differenceInMinutes == 1 ? "minute" : "minutes") + " ago";
  else {
    const differenceInHours = Math.floor(differenceInMilliseconds / (1000 * 60 * 60));
    if(differenceInHours < 24)
      return differenceInHours + " " + (differenceInHours == 1 ? "hour" : "hours") + " ago";
    else {
      const differenceInDays = Math.floor(differenceInMilliseconds / (1000 * 60 * 60 * 24));
      return differenceInDays + " " + (differenceInDays == 1 ? "day" : "days") + " ago";
    }
  }
}

export const MonitoringPanel = ({mode}) => {
  const [applications, setApplications] = useState([]);
  const [nodegroups, setNodegroups] = useState([]);
  const [curItem, setCurItem] = useState("");
  const [filterOptions, setFilterOptions] = useState();
  const [nodeOptions, setNodeOptions] = useState([]);
  const { state, dispatch } = useResourceContext();
  const { selectedResources: [selectedResource] } = state;
  const id = selectedResource?.id;
  const [scope, setScope] = useState(getInitialScope(selectedResource, mode));
  const invalidate = useInvalidate();
  //const type = selectedInstance.type;

  const nodeGroupsOptions = [
    {
      "value": "-master",
      "label": "master"
    }, {
      "value": "-worker",
      "label": "worker"
    }, {
      "value": "-database",
      "label": "database"
    }
  ]

  const helmOptions = [
    {
      "value": "zookeeper-",
      "label": "zookeeper"
    }, {
      "value": "hadoop-hdfs-",
      "label": "hadoop"
    }, {
      "value": "spark-",
      "label": "spark"
    }, {
      "value": "mysql-",
      "label": "mysql"
    }, {
      "value": "via-",
      "label": "via"
    }
  ]

  const onScopeChange = (val)=> {
    setScope(val);
    setCurItem(null);
    setNodeOptions(null);
    setFilterOptions(null);
  }

  const handleChange = (value) => {
    setCurItem(value);
  };

  const onFilterChange =(val)=> {
    setCurItem(null);
    setFilterOptions(val);
  };

  return (
    <div>
      <Flex justify="space-between" align="center">
      <Space size="middle">
      {"Scope"} 
      <Select value={scope} onChange={onScopeChange} options= {[
        {
          value: 'nodes',
          label: 'Nodes',
        },
        {
          value: 'pods',
          label : 'Pods'
        }
      ]} style={{ width: 120 }} disabled={"instance" !== mode}/>
      {"nodes" == scope ? "NodeGroups" : "Helm"}
      {"nodes" == scope ? 
        <Select allowClear key="nodegroup" maxTagCount="responsive" options= {nodeGroupsOptions} style={{ width: 180 }} onChange={onFilterChange}/> : 
        <Select allowClear key="helm" maxTagCount="responsive" options= {helmOptions} style={{ width: 180 }} onChange={onFilterChange}/>
      }
      {"nodes" == scope ? "Node" : "Pod"}
      {"nodes" == scope ? 
        <Select allowClear key="node" value={curItem} maxTagCount="responsive" options= {nodeOptions} style={{ width: 320 }} onChange={handleChange}/> : 
        <Select allowClear key="pod" value={curItem} maxTagCount="responsive" options= {nodeOptions} style={{ width: 320 }} onChange={handleChange}/>
      }
      
      </Space>
      { !curItem && <RefreshButton 
        onClick={() => {
          invalidate({
            resource: "monitorings",
            invalidates: ["list"],
          });
        }}
      />}
      </Flex>
      {"nodes" == scope && (curItem ? <NodePanel curItem={curItem} setCurItem={setCurItem} setScope={setScope}/> : <NodesPanel mode={mode} selectedResource={selectedResource} setCurItem={setCurItem} setNodeOptions={setNodeOptions} filterOptions={filterOptions} setScope={setScope}/>)}
      {"pods" == scope && (curItem ? <PodPanel curItem={curItem} namespace={selectedResource.namespace} /> : <PodsPanel mode={mode} selectedResource={selectedResource} setCurItem={setCurItem} setNodeOptions={setNodeOptions} filterOptions={filterOptions} setScope={setScope}/>)}
    </div>
  );
}; 

const NodesPanel = ({mode, selectedResource, setCurItem, setNodeOptions, filterOptions, setScope}) => {
  const [options, setOptions] = useState([]);
  const [filters, setFilters] = useState(getInitialFilter(selectedResource, mode, true));

  const { data, isLoading } = useList({
    resource: "monitorings",
    syncWithLocation: false,
    pagination: { current: 1, pageSize: 10 },
    meta: {"action":"node"},
    filters: filters
  });

  useEffect(() => {
    if (
      !isLoading &&
      data?.data &&
      data.data.length > 0 
    ) {
      setNodeOptions(data.data.map(item => {return {"value": item.objectMeta.name, "label": item.objectMeta.name}}));
      return;
    }
  }, [isLoading, data]);

  useEffect(() => {
    const initialFilter = getInitialFilter(selectedResource, mode, true);
    if(filterOptions) {
      setFilters([...initialFilter, {field: 'nodegroup', operator: 'contains', value: [filterOptions]}], "replace");
    } else {
      setFilters(initialFilter, "replace");
    }
  }, [selectedResource.id, filterOptions]);

  const nodesColumns = [
    {
      title: 'Name',
      key: 'name',
      render: (text, record) => { return <a onClick={(e) => {setCurItem(record.objectMeta.name)}}>{record.objectMeta.name}</a>;}
    },
    {
      title: 'CPU Usage(cores)',
      render: (text, record) => { return <WaveformSVG data={record.metrics[0].dataPoints.map((item) => item.y)} mode = {1} text={(record.metrics[0].dataPoints.slice(-1)[0].y).toFixed(2) + "m" }/>},
      key: 'cpuUsage'
    },
    {
      title: 'Memory Usage(bytes)',
      render: (text, record) => { return <WaveformSVG data={record.metrics[1].dataPoints.map((item) => item.y)} mode = {2} text={toGUnit(record.metrics[1].dataPoints.slice(-1)[0].y) + "Gi" }/>},
      key: 'memoryUsage'
    },
    {
      title: 'Ready',
      key: 'ready',
      dataIndex: 'ready'
    },
    {
      title: 'CPU Request (cores)',
      render: (text, record) => { return record.allocatedResources.cpuRequests + '(' + record.allocatedResources.cpuRequestsFraction.toFixed(2) + '%)';},
      key: 'cpuRequest'
    },
    {
      title: 'CPU Limits (cores)',
      render: (text, record) => { return record.allocatedResources.cpuLimits + ' (' + record.allocatedResources.cpuLimitsFraction.toFixed(2) + '%)';},
      key: 'cpuRequest'
    },
    {
      title: 'CPU Capacity (cores)',
      render: (text, record) => { return record.allocatedResources.cpuCapacity;},
      key: 'cpuCapacity'
    },
    {
      title: 'Memory Request (G)',
      render: (text, record) => { return toGUnit(record.allocatedResources.memoryRequests) + ' (' + record.allocatedResources.memoryRequestsFraction.toFixed(2) + '%)';},
      key: 'cpuRequest'
    },
    {
      title: 'Memory Limits (G)',
      render: (text, record) => { return toGUnit(record.allocatedResources.memoryLimits) + ' (' + record.allocatedResources.memoryLimitsFraction.toFixed(2) + '%)';},
      key: 'cpuRequest'
    },
    {
      title: 'Memory Capacity (G)',
      render: (text, record) => { return toGUnit(record.allocatedResources.memoryCapacity) + 'G';},
      key: 'cpuRequest'
    },
    {
      title: 'Pods',
      render: (text, record) => { return record.allocatedResources.allocatedPods + ' (' + record.allocatedResources.podFraction.toFixed(2) + '%)';},
      key: 'cpuRequest'
    },
    {
      title: "Created",
      render: (text, record) => {
        const createdDate = new Date(record.objectMeta.creationTimestamp);
        const label = calculateTimeDifference(createdDate);
        return <Tooltip title={createdDate.toLocaleString()}><span style={{textDecoration: 'underline', textDecorationStyle: 'dotted'}}>{label}</span></Tooltip>
      },
      key: 'created'
    }
  ];

  return <Table loading={isLoading} dataSource={data?.data} columns={nodesColumns} />;
}

const getPodColumnsTP = (setScope, setCurItem) => {
  return [
    {
      title: 'Images',
      key: 'images',
      width: 150,
      render: (text, record) => { return <DataList data={record.containerImages} />; }
    },
    {
      title: 'Node',
      key: 'node',
      render: (text, record) => { return <a onClick={(e) => {setScope("nodes");setCurItem(record.nodeName)}}>{record.nodeName}</a>;}
    }, {
      title: 'Status',
      key: 'status', 
      dataIndex: 'status'
    }, {
      title: 'Restarts',
      key: 'restarts', 
      dataIndex: 'restartCount'
    }
  ];
}

const PodsPanel = ({mode, curItem, setCurItem, selectedResource, setNodeOptions, filterOptions, setScope}) => {
  // const {data, isLoading} = useList({
  //   resource: "monitorings",
  //   meta:{action: "pods"},
  //   pagination:{current:1, pageSize:10},
  // })
  const { tableProps, filters, setFilters } = useTable({
    resource: "monitorings",
    syncWithLocation: false,
    pagination: { current: 1, pageSize: 10 },
    meta: {"action":"pod"},
    filters: {
      initial : getInitialFilter(selectedResource, mode, false)
    }
  });
  // const data = [{"objectMeta":{"name":"via-all-0","namespace":"i-uplm69b3","labels":{"app":"via","apps.kubernetes.io/pod-index":"0","component":"all-in-one","controller-revision-hash":"via-all-588c4475f5","release":"via","statefulset.kubernetes.io/pod-name":"via-all-0"},"creationTimestamp":"2024-12-30T08:33:14Z","uid":"e24a5d8a-6df2-418b-8cdd-056a20adbbfa","ownerReferences":[{"kind":"StatefulSet","name":"via-all"}]},"typeMeta":{"kind":"pod"},"status":"Running","restartCount":0,"metrics":[{"dataPoints":[{"x":1735558016,"y":12},{"x":1735558076,"y":11},{"x":1735558136,"y":15},{"x":1735558196,"y":11},{"x":1735558256,"y":10},{"x":1735558316,"y":10},{"x":1735558376,"y":11},{"x":1735558436,"y":11},{"x":1735558496,"y":11},{"x":1735558556,"y":10},{"x":1735558616,"y":18},{"x":1735558676,"y":10},{"x":1735558736,"y":11},{"x":1735558796,"y":11},{"x":1735558856,"y":10}],"metricPoints":[{"timestamp":"2024-12-30T11:26:56Z","value":12},{"timestamp":"2024-12-30T11:27:56Z","value":11},{"timestamp":"2024-12-30T11:28:56Z","value":15},{"timestamp":"2024-12-30T11:29:56Z","value":11},{"timestamp":"2024-12-30T11:30:56Z","value":10},{"timestamp":"2024-12-30T11:31:56Z","value":10},{"timestamp":"2024-12-30T11:32:56Z","value":11},{"timestamp":"2024-12-30T11:33:56Z","value":11},{"timestamp":"2024-12-30T11:34:56Z","value":11},{"timestamp":"2024-12-30T11:35:56Z","value":10},{"timestamp":"2024-12-30T11:36:56Z","value":18},{"timestamp":"2024-12-30T11:37:56Z","value":10},{"timestamp":"2024-12-30T11:38:56Z","value":11},{"timestamp":"2024-12-30T11:39:56Z","value":11},{"timestamp":"2024-12-30T11:40:56Z","value":10}],"metricName":"cpu/usage_rate","aggregation":"sum"},{"dataPoints":[{"x":1735558016,"y":12857151488},{"x":1735558076,"y":12856975360},{"x":1735558136,"y":12622692352},{"x":1735558196,"y":12622909440},{"x":1735558256,"y":12622630912},{"x":1735558316,"y":12622884864},{"x":1735558376,"y":12622843904},{"x":1735558436,"y":12622737408},{"x":1735558496,"y":12623138816},{"x":1735558556,"y":12622606336},{"x":1735558616,"y":12573102080},{"x":1735558676,"y":12573106176},{"x":1735558736,"y":12573130752},{"x":1735558796,"y":12573450240},{"x":1735558856,"y":12573061120}],"metricPoints":[{"timestamp":"2024-12-30T11:26:56Z","value":12857151488},{"timestamp":"2024-12-30T11:27:56Z","value":12856975360},{"timestamp":"2024-12-30T11:28:56Z","value":12622692352},{"timestamp":"2024-12-30T11:29:56Z","value":12622909440},{"timestamp":"2024-12-30T11:30:56Z","value":12622630912},{"timestamp":"2024-12-30T11:31:56Z","value":12622884864},{"timestamp":"2024-12-30T11:32:56Z","value":12622843904},{"timestamp":"2024-12-30T11:33:56Z","value":12622737408},{"timestamp":"2024-12-30T11:34:56Z","value":12623138816},{"timestamp":"2024-12-30T11:35:56Z","value":12622606336},{"timestamp":"2024-12-30T11:36:56Z","value":12573102080},{"timestamp":"2024-12-30T11:37:56Z","value":12573106176},{"timestamp":"2024-12-30T11:38:56Z","value":12573130752},{"timestamp":"2024-12-30T11:39:56Z","value":12573450240},{"timestamp":"2024-12-30T11:40:56Z","value":12573061120}],"metricName":"memory/usage","aggregation":"sum"}],"warnings":[],"nodeName":"ip-192-168-27-39.ec2.internal","containerImages":["083932473731.dkr.ecr.us-east-1.amazonaws.com/vitria/viaops:6.1.0.21-v4.0_MT"],"containerStatuses":[{"name":"via-all","state":"Running","ready":true}],"allocatedResources":{"cpuRequests":8000,"cpuLimits":8000,"memoryRequests":73014444032,"memoryLimits":73014444032}}];
  // const isLoading = false;
  
  // useEffect(() => {
  //   if (
  //     !tableProps.loading &&
  //     tableProps.dataSource &&
  //     tableProps.dataSource.length > 0 
  //   ) {
  //     setNodeOptions(tableProps.dataSource.map(item => {return {"value": item.objectMeta.name, "label": item.objectMeta.name}}));
  //     return;
  //   }
  // }, [tableProps.loading, tableProps.dataSource]);
  useEffect(() => {
    if(!tableProps.loading && tableProps.dataSource && tableProps.dataSource.length > 0)
      setNodeOptions(tableProps.dataSource.map(item => {return {"value": item.objectMeta.name, "label": item.objectMeta.name}}));
  }, [tableProps.loading, tableProps.dataSource]);

  useEffect(() => {
    const initialFilter = getInitialFilter(selectedResource, mode, false);
    if(filterOptions) {
      setFilters([...initialFilter, {field: 'name', operator: 'contains', value: [filterOptions]}], "replace");
    } else {
      setFilters(initialFilter, "replace");
    }
  }, [filterOptions]);

  const podsColumns = [
    {
      title: '',
      key: 'status',
      render: (text, record) => {
        var color = (record.status == "Running" || record.status == "Succeeded") ? 'green' : 'red';
        return <Badge dot style={{ backgroundColor: color, width: '15px', height: '15px'}} />
      }
    },
    {
      title: 'Name',
      key: 'name',
      render: (text, record) => { return <a onClick={(e) => {setCurItem(record.objectMeta.name)}}>{record.objectMeta.name}</a>;}
    }, ...getPodColumnsTP(setScope, setCurItem), 
    {
      title: 'CPU Usage(cores)',
      render: (text, record) => { return <WaveformSVG data={record.metrics[0].dataPoints.map((item) => item.y)} mode={1} text={record.metrics[0].dataPoints.slice(-1)[0]?.y.toFixed(2) + "m" }/>},
      key: 'cpuUsage'
    },
    {
      title: 'Memory Usage(bytes)',
      render: (text, record) => { return <WaveformSVG data={record.metrics[1].dataPoints.map((item) => item.y)} mode={2} text={(record.metrics[1].dataPoints.slice(-1)[0]?.y/1024/1024).toFixed(2) + "Mi"}/>},
      key: 'memoryUsage'
    },
    {
      title: "Created",
      render: (text, record) => {
        const createdDate = new Date(record.objectMeta.creationTimestamp);
        const label = calculateTimeDifference(createdDate);
        return <Tooltip title={createdDate.toLocaleString()}><span style={{textDecoration: 'underline', textDecorationStyle: 'dotted'}}>{label}</span></Tooltip>
      },
      key: 'created'
    }
  ]
  return (
    <div>
      <Table {...tableProps} columns={podsColumns}/>
    </div>
  )
}

const PodPanel = ({curItem, namespace})=> {
  const { data, isLoading, isError }  = useOne({
    resource: "monitorings",
    id: curItem,
    meta: {"action":"pod", "namespace": namespace}
  })

  const cpuMetricsConfig = {
    title: "CPU Usage",
    data: data?.metrics[0].dataPoints || [],
    xField: (d) => new Date(d.x * 1000),
    yField: (d) => d.y,
    height: 200,
    shapeField: 'smooth',
    point: {
      shapeField: 'square',
      sizeField: 2,
      tooltip: false
    },
    interaction: {
      tooltip: {
        marker: false,
      },
    },
    axis: {
      x: {
        grid: true,
        gridLineWidth: 2,
        gridLineDash: [0,0]
      },
      y: {
        title: 'CPU (Cores)',
        style: {
          fontSize: 16,   // 设置字体大小
          fontWeight: 'bold',  // 设置字体加粗
          fill: '#333',   // 设置字体颜色
        },
        grid: true,
        gridLineWidth: 2,
        gridLineDash: [0,0]
      },
    },
    style: {
      lineWidth: 1,
      fill: 'l(90) 0:#36D076 1:#94E8B7',
      opacity: '0.8'
    }
  }

  const memoryMetricsConfig = {
    title: "Memory Usage",
    data: data?.metrics[1].dataPoints || [],
    xField: (d) => new Date(d.x * 1000),
    yField: (d) => d.y/1024/1024,
    height: 200,
    shapeField: 'smooth',
    point: {
      shapeField: 'square',
      sizeField: 2,
      tooltip: false
    },
    interaction: {
      tooltip: {
        marker: false,
      },
    },
    axis: {
      x: {
        grid: true,
        gridLineWidth: 2,
        gridLineDash: [0,0]
      },
      y: {
        title: 'Memory (Bytes)',
        style: {
          fontSize: 16,   // 设置字体大小
          fontWeight: 'bold',  // 设置字体加粗
          fill: '#333',   // 设置字体颜色
        },
        grid: true,
        gridLineWidth: 2,
        gridLineDash: [0,0]
      },
    },
    style: {
      lineWidth: 1,
      fill: 'l(90) 0:#608EEC 1:#AAC2F4',
    }
  }

  const processColumns = [
    {
      title: 'Process Name',
      key: 'name',
      dataIndex: 'Name'
    }, {
      title: 'Type',
      key: 'type',
      dataIndex: 'Type'
    }, {
      title: 'User',
      key: 'user',
      dataIndex: 'User'
    }, {
      title: 'Command',
      key: 'command',
      dataIndex: 'COMMAND'
    },
    {
      'title': 'CPU (%)',
      key: "cpu",
      dataIndex: '%CPU'
    },
    {
      'title': 'CPU Max (%)',
      key: "cpuMax",
      dataIndex: '%CPU_MAX'
    }, {
      title: 'CPU Usage (cores)',
      render: (text, record) => { return <WaveformSVG data={record.metrics? record.metrics[0].dataPoints.map((item) => item.y) : []} mode={1} text={record.metrics ? record.metrics[0].dataPoints.slice(-1)[0].y.toFixed(2) + "m" : ""}/>},
      key: 'cpuUsage'
    },
    {
      title: 'Memory Usage (bytes)',
      render: (text, record) => { return <WaveformSVG data={record.metrics? record.metrics[1].dataPoints.map((item) => item.y) : []} mode={2} text={record.metrics ? (record.metrics[1].dataPoints.slice(-1)[0].y/1024/1024).toFixed(2) + "Mi" : ""}/>},
      key: 'memoryUsage'
    }
  ]

  return isLoading ? ("Loading...") : (<div>
    {/* <Row gutter={40} justify="center" style={{heigh: '200px'}}>
      <Col span={12}>
        <Area {...cpuMetricsConfig}/>
      </Col>
      <Col span={12}>
        <Area {...memoryMetricsConfig}/>
      </Col>
    </Row> */}

    <MetricsChart metricsData={data.metrics}/>


    {data.processes && <div>
      <Table dataSource={data.processes} columns={processColumns}/>
    </div>  }
  </div>)
}

const NodePanel = ({curItem, setCurItem, setScope}) => {

  const { data, isLoading, isError }  = useOne({
    resource: "monitorings",
    id: curItem,
    meta: {"action":"node"}
  })

  const podsColumns = [
    {
      title: 'Name',
      key: 'name',
      render: (text, record) => { return <a onClick={(e) => {setScope("pods"); setCurItem(record.objectMeta.name)}}>{record.objectMeta.name}</a>;}
    }, ...getPodColumnsTP(setScope, setCurItem), 
    {
      title: 'CPU Usage(cores)',
      render: (text, record) => { return <WaveformSVG data={record.metrics.cpuUsageHistory.map((item) => item.value)} mode={1} text={record.metrics.cpuUsage.toFixed(2) + "m" }/>},
      key: 'cpuUsage'
    },
    {
      title: 'Memory Usage(bytes)',
      render: (text, record) => { return <WaveformSVG data={record.metrics.memoryUsageHistory.map((item) => item.value)} mode={2} text={(record.metrics.memoryUsage/1024/1024).toFixed(2) + "Mi"}/>},
      key: 'memoryUsage'
    },
    {
      title: "Created",
      render: (text, record) => {
        const createdDate = new Date(record.objectMeta.creationTimestamp);
        const label = calculateTimeDifference(createdDate);
        return <Tooltip title={createdDate.toLocaleString()}><span style={{textDecoration: 'underline', textDecorationStyle: 'dotted'}}>{label}</span></Tooltip>
      },
      key: 'created'
    }
  ]

  const cpuData = {
    title: "CPU",
    dataArray: [{
      data: [{
        x: "usage",
        y: 100 - data?.allocatedResources.cpuRequestsFraction
      }, {
        x: "free",
        y: data?.allocatedResources.cpuRequestsFraction
      }],
      ctext1: data?.allocatedResources.cpuRequestsFraction.toFixed(1),
      ctext2: "Requests",
      btext: "Cores: " + (data?.allocatedResources.cpuRequests/1000).toFixed(1),
    }, {
      data: [{
        x: "usage",
        y: 100 - data?.allocatedResources.cpuLimitsFraction
      }, {
        x: "free",
        y: data?.allocatedResources.cpuLimitsFraction
      }],
      ctext1: data?.allocatedResources.cpuLimitsFraction.toFixed(1),
      ctext2: "Limits",
      btext: "Cores: " + (data?.allocatedResources.cpuLimits/1000).toFixed(1),
  }]};

  const memoryData = {
    title: "Memory",
    dataArray: [{
      data: [{
        x: "usage",
        y: 100 - data?.allocatedResources.memoryRequestsFraction
      }, {
        x: "free",
        y: data?.allocatedResources.memoryRequestsFraction
      }],
      ctext1: data?.allocatedResources.memoryRequestsFraction.toFixed(1),
      ctext2: "Requests",
      btext: "GiB: " + toGUnit(data?.allocatedResources.memoryRequests),
    }, {
      data: [{
        x: "usage",
        y: 100 - data?.allocatedResources.memoryLimitsFraction
      }, {
        x: "free",
        y: data?.allocatedResources.memoryLimitsFraction
      }],
      ctext1: data?.allocatedResources.memoryLimitsFraction.toFixed(1),
      ctext2: "Limits",
      btext: "GiB: " + toGUnit(data?.allocatedResources.memoryLimits),
  }]};

  const podData = {
    title: "Pods",
    dataArray: [{
      data: [{
        x: "usage",
        y: 100 - data?.allocatedResources.podFraction
      }, {
        x: "free",
        y: data?.allocatedResources.podFraction
      }],
      ctext1: data?.allocatedResources.podFraction.toFixed(1),
      ctext2: "Requests",
      btext: "Pods: " + data?.allocatedResources.allocatedPods,
    }]};

  const cpuMetricsConfig = {
    title: "CPU Usage",
    data: data?.metrics[0].dataPoints || [],
    xField: (d) => new Date(d.x * 1000),
    yField: (d) => d.y/1000,
    height: 200,
    shapeField: 'smooth',
    point: {
      shapeField: 'square',
      sizeField: 2,
      tooltip: false
    },
    interaction: false,
    axis: {
      x: {
        grid: true,
        gridLineWidth: 2,
        gridLineDash: [0,0]
      },
      y: {
        title: 'CPU (Cores)',
        style: {
          fontSize: 16,   
          fontWeight: 'bold', 
          fill: '#333', 
        },
        grid: true,
        gridLineWidth: 2,
        gridLineDash: [0,0]
      },
    },
    style: {
      lineWidth: 1,
      fill: 'l(90) 0:#36D076 1:#94E8B7',
      opacity: '0.8'
    }
  }

  const memoryMetricsConfig = {
    title: "Memory Usage",
    data: data?.metrics[1].dataPoints || [],
    xField: (d) => new Date(d.x * 1000),
    yField: (d) => d.y/1024/1024/1024,
    height: 200,
    shapeField: 'smooth',
    point: {
      shapeField: 'square',
      sizeField: 2,
      tooltip: false
    },
    interaction: {
      tooltip: {
        marker: false,
      },
    },
    axis: {
      x: {
        grid: true,
        gridLineWidth: 2,
        gridLineDash: [0,0]
      },
      y: {
        title: 'Memory (Bytes)',
        style: {
          fontSize: 16,   // 设置字体大小
          fontWeight: 'bold',  // 设置字体加粗
          fill: '#333',   // 设置字体颜色
        },
        grid: true,
        gridLineWidth: 2,
        gridLineDash: [0,0]
      },
    },
    style: {
      lineWidth: 1,
      fill: 'l(90) 0:#608EEC 1:#AAC2F4',
    }
  }

  return isLoading ? ("Loading...") :(
    <div>
      <Space size="large" direction="vertical">
      <Row gutter={40} justify="center">
        <Col span={8}>
          <DonutChart cdata={cpuData}/>
        </Col>
        <Col span={8}>
          <DonutChart cdata={memoryData}/>
        </Col>
        <Col span={6}>
          <DonutChart cdata={podData}/>
        </Col>
      </Row>
      {/* <Row gutter={40} justify="center" style={{heigh: '200px'}}>
        <Col span={12}>
          <Area {...cpuMetricsConfig}/>
        </Col>
        <Col span={12}>
          <Area {...memoryMetricsConfig}/>
        </Col>
      </Row>       */}
      <MetricsChart metricsData={data.metrics}/>
      <Table dataSource={data.podList.pods} columns={podsColumns}/>    
      </Space>  
    </div>
  )
}